import { LegalLayout } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/constants/site";

export default function Privacy() {
  const email = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  return (
    <LegalLayout
      testId="privacy-page"
      title="Privacy Policy"
      updated="June 1, 2026"
      intro="This Privacy Policy explains what information Aura Live collects, how we use it, and the choices you have. Aura Live is a location-based live-streaming service — your location is a public signal while you use it."
    >
      <p>
        This privacy policy applies to the Aura Live app for mobile devices,
        together with any related services operated by Mapcast BV, a company
        registered in the Netherlands with its registered office at Herengracht
        320, 1016 CE Amsterdam, The Netherlands (collectively, the
        "Application"). Mapcast BV, operating as Aura Live, is hereby referred to
        as the "Service Provider" and is the data controller for the purposes of
        this policy. This policy is effective as of 1 June 2026.
      </p>

      <h2>Information Collection and Use</h2>
      <p>The Application collects information when you download and use it. This information may include:</p>
      <ul>
        <li>Your device's Internet Protocol (IP) address</li>
        <li>The pages of the Application that you visit, the time and date of your visit, and the time spent on those pages</li>
        <li>The time spent on the Application</li>
        <li>The mobile operating system you use</li>
        <li>Date of birth</li>
      </ul>

      <h2>Date of Birth</h2>
      <p>
        We collect your date of birth at first sign-in for the sole purpose of
        verifying you are at least 18 years old. We store the date of birth on your
        user record; we do not share it with third parties, use it for advertising,
        or use it for profiling. You may request deletion of your DOB by contacting
        us — deletion will terminate your ability to broadcast or purchase in-app
        coins.
      </p>
      <p>
        <strong>Retention:</strong> your date of birth is retained for the duration
        of your account plus up to 7 years thereafter, solely for compliance audit
        purposes (App Store and EU age-verification obligations), after which it is
        permanently deleted.
      </p>

      <h2>Cookies and Tracking Technologies</h2>
      <p>
        The Application or its third-party SDKs may use cookies, SDKs, pixels and
        similar technologies to support functionality, analytics or service
        delivery. Where required by applicable law, the Service Provider will
        obtain consent before using non-essential tracking technologies.
      </p>

      <h2>Location — Public by Design</h2>
      <p>Aura Live is a location-based broadcasting service. Your location is a public signal while you use it. Specifically:</p>
      <ul>
        <li><strong>While broadcasting live:</strong> your precise, real-time location is transmitted to our streaming provider and displayed on the map to all viewers who can see your stream (public, follower-only, or a limited audience depending on your settings).</li>
        <li><strong>While offline:</strong> your last known coarse location (rounded to approximately 1 km precision) may remain visible on the map as an offline host pin, so viewers can discover your area between broadcasts.</li>
        <li>When another user searches or discovers streams near you, your coarse location is compared against theirs so we can surface nearby live activity.</li>
      </ul>
      <p>You control this. You can:</p>
      <ul>
        <li>Deny location permission entirely at the OS level (Settings → Privacy → Location); the app will still work but you won't appear on the map or be discovered by nearby viewers.</li>
        <li>Turn off "share coarse location while offline" in the Profile screen (planned setting).</li>
        <li>Delete your last location by signing out and confirming account deletion.</li>
      </ul>
      <p>
        We do not share your precise location with advertisers or analytics
        providers. Coarse location may be aggregated (e.g. "100 hosts live in
        Istanbul today") for aggregate statistics that do not identify you.
      </p>

      <h2>Personally Identifiable Information</h2>
      <p>For a better experience while using the Application, the Service Provider may require you to provide certain personally identifiable information, including but not limited to:</p>
      <ul>
        <li>Email</li>
        <li>Name</li>
        <li>Location</li>
        <li>User-Generated Content (video / audio / chat)</li>
        <li>Purchase History</li>
        <li>Device ID</li>
      </ul>
      <p>
        The information the Service Provider requests will be retained and used as
        described in this privacy policy. The Service Provider may use the
        information you provide to send important information, required notices,
        and, where permitted by law, marketing communications.
      </p>

      <h2>Third-Party Sub-Processors</h2>
      <p>To operate Aura Live we share limited personal information with the following sub-processors. Each has its own privacy policy governing its use of your data.</p>
      <table>
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Purpose</th>
            <th>Data shared</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Real-time streaming provider</td>
            <td>Real-time live video & audio streaming</td>
            <td>Camera and microphone streams, IP address, device identifier</td>
          </tr>
          <tr>
            <td>Google LLC — Sign In & Maps SDK</td>
            <td>OAuth authentication, map rendering</td>
            <td>Email, name, profile picture, coarse location</td>
          </tr>
          <tr>
            <td>Apple Inc. — Sign in with Apple & In-App Purchase</td>
            <td>Authentication, payment processing</td>
            <td>Email (relayed), purchase receipts</td>
          </tr>
          <tr>
            <td>Backend infrastructure & push notifications</td>
            <td>Application hosting, notification delivery</td>
            <td>Account data, device push tokens, application logs</td>
          </tr>
          <tr>
            <td>MongoDB, Inc. (Atlas)</td>
            <td>Encrypted database storage</td>
            <td>All account and application data</td>
          </tr>
        </tbody>
      </table>
      <p>
        We do not sell your personal information to any of these parties. Each
        sub-processor is contractually obligated to process your data only for the
        purposes listed above.
      </p>

      <h2>How Live Broadcasts Are Handled</h2>
      <p>When you tap <strong>Go Live</strong>:</p>
      <ul>
        <li>Video and audio are streamed in real time to viewers through our streaming provider. These streams are not recorded by Aura Live today. The provider may briefly buffer streams for delivery but does not persist them on our behalf. If we introduce recording in the future, we will update this policy and require your affirmative consent before enabling it for you.</li>
        <li>A single still-frame poster may be captured from your camera at the moment you start broadcasting and stored on our servers as your broadcast thumbnail. This poster continues to be shown on the map after the broadcast ends until it is replaced by a newer one.</li>
        <li>Chat messages exchanged during your broadcast are stored on our servers to support delivery to late-arriving viewers and moderation. Chat is retained for 30 days after the broadcast ends, then deleted.</li>
        <li>Your live location is transmitted to the streaming provider and displayed to viewers as long as the broadcast is active. When the broadcast ends, your coarse (city-level) last-known location remains visible on the map for discovery, until you delete your account or change your location manually.</li>
      </ul>
      <p>By tapping Go Live you understand that anything captured by your camera, microphone or in the chat may be seen and screen-recorded by any viewer.</p>

      <h2>Payment Information</h2>
      <p>
        Aura Live uses Apple's App Store In-App Purchase and Google Play Billing to
        process all coin purchases. We never see or store your credit-card,
        debit-card or bank-account details — those are handled exclusively by Apple
        or Google under their own privacy policies. After a purchase completes:
      </p>
      <ul>
        <li>We receive a purchase receipt from Apple or Google (transaction ID, product ID, timestamp, amount) which we validate server-side to credit coins to your account.</li>
        <li>We store this receipt data on your account for 7 years to comply with tax and audit obligations.</li>
        <li>We log every in-app coin movement (top-up, gift, tip, host earning) as a transaction record; you can view your own transaction history in the Wallet screen and request deletion when it is no longer required for tax or fraud purposes.</li>
      </ul>
      <p>
        If you are a host earning coins, additional financial disclosures may apply
        when we introduce cash-outs; those will be described in a supplementary
        payments agreement at that time.
      </p>

      <h2>Your Rights</h2>
      <p>
        You may request access to, correction of, or deletion of your personal data
        held by the Service Provider. To exercise these rights, or to withdraw
        consent where processing is based on consent, contact the Service Provider
        at {email}.
      </p>

      <h2>Your California Privacy Rights (CCPA / CPRA)</h2>
      <p>
        If you are a California resident, you have the right to know what personal
        information is collected, the right to delete personal information, the
        right to opt out of the sale or sharing of personal information, and the
        right to non-discrimination for exercising these rights. To exercise your
        CCPA/CPRA rights, contact the Service Provider at {email}.
      </p>

      <h2>International Data Transfers</h2>
      <p>The Service Provider or its third-party service providers may transfer personal data to countries outside your country of residence, including outside the European Economic Area (EEA). Where applicable law requires safeguards for international transfers, the Service Provider will use appropriate mechanisms:</p>
      <ul>
        <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
        <li>Adequacy decisions or other legally recognized transfer mechanisms</li>
        <li>Your consent, where required and legally permitted</li>
      </ul>
      <p>Data protection laws in other countries may differ from those in your jurisdiction. Where required by law, the Service Provider will apply appropriate safeguards and obtain any consent required for the transfer.</p>

      <h2>Information Disclosure</h2>
      <p>The Service Provider may disclose User Provided and Automatically Collected Information:</p>
      <ul>
        <li>as required by law, such as to comply with a subpoena or similar legal process;</li>
        <li>when they believe in good faith that disclosure is necessary to protect their rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;</li>
        <li>with trusted service providers who work on their behalf, do not have an independent use of the information disclosed to them, and have agreed to adhere to the rules set forth in this privacy statement.</li>
      </ul>

      <h2>Opt-Out Rights</h2>
      <p>
        You can stop further collection of information from your mobile device by
        uninstalling the Application. Uninstalling will stop the Application from
        collecting data from your device, but it does not automatically delete
        information that has already been transmitted to the Service Provider or to
        third parties. To request deletion of your personal data, to withdraw
        consent, or to exercise any of your rights, contact the Service Provider at
        {" "}{email}.
      </p>

      <h2>Data Retention Policy</h2>
      <p>The Service Provider retains personal data based on its necessity for the stated purposes:</p>
      <ul>
        <li><strong>User Provided Data:</strong> retained for the duration of your use of the Application plus 12 months thereafter, unless longer retention is required by law.</li>
        <li><strong>Automatically Collected Data:</strong> retained for up to 24 months from collection, unless longer retention is required for legal compliance.</li>
        <li><strong>Aggregated and Anonymized Data:</strong> retained indefinitely as it no longer identifies you.</li>
        <li><strong>Data required for legal compliance:</strong> retained as long as required by applicable law.</li>
      </ul>
      <p>
        You may request deletion of your personal data, subject to any legal
        obligation to retain it. If you want the Service Provider to delete User
        Provided Data submitted through the Application, please contact them at{" "}
        {email}. Please note that some User Provided Data may be required for the
        Application to function properly.
      </p>

      <h2>Data Deletion</h2>
      <p>
        You can request deletion of your personal data or account by contacting the
        Service Provider at {email}. The Service Provider will process your request
        within the timeframes required by applicable law. Upon verification of your
        identity, the Service Provider will delete your personal data from its
        systems, except where retention is required for legal compliance or
        legitimate business purposes.
      </p>

      <h2>Age Requirement — 18+ Only</h2>
      <p>
        Aura Live is intended solely for users aged 18 or older. You may not create
        an account, broadcast, tip, purchase in-app coins or send gifts unless you
        are 18. To enforce this, we collect your date of birth during onboarding
        and refuse the requested action if you are under 18. We do not knowingly
        collect personal information from anyone under 18. If we discover that we
        have inadvertently collected data from a minor, we will delete that account
        and all associated data within 30 days. If you believe a minor has provided
        data through the app, please email {email} and we will act promptly.
      </p>

      <h2>Security</h2>
      <p>
        The Service Provider is concerned about safeguarding the confidentiality of
        your information. The Service Provider provides physical, electronic and
        procedural safeguards to protect information the Service Provider processes
        and maintains.
      </p>

      <h2>Data Breach Notification</h2>
      <p>
        If a data breach occurs that affects your personal data, the Service
        Provider will notify you in accordance with applicable legal requirements,
        including, where required, providing information about the nature of the
        breach and the steps being taken to address it.
      </p>

      <h2>Changes</h2>
      <p>
        The Service Provider may update this Privacy Policy from time to time. The
        Service Provider will notify you of material changes by posting the updated
        Privacy Policy with an effective date. Where required by law, the Service
        Provider will seek your consent to material changes before they take effect.
        Previous versions of this Privacy Policy will be maintained and made
        available upon request by contacting the Service Provider at {email}. This
        privacy policy is effective as of 1 June 2026.
      </p>

      <h2>Your Consent</h2>
      <p>
        Where processing is based on consent, you provide that consent by
        affirmatively opting in to the relevant feature or action. You may withdraw
        consent at any time without affecting processing carried out before
        withdrawal. Processing based on other lawful bases is carried out as
        described above.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions regarding privacy while using the Application, or
        have questions about our practices, please contact the Service Provider via
        email at {email}, or by post at:
      </p>
      <p>
        <strong>Mapcast BV</strong><br />
        Herengracht 320<br />
        1016 CE Amsterdam, The Netherlands
      </p>
    </LegalLayout>
  );
}
